/**
 * Player settings stored in `play.profile` (runebound-app migration 0041: `default_tier` plus a
 * `settings` jsonb whose shape play.runebound owns). Defaults follow owner decision Q5 (tier 2 by default;
 * auto-draw/channel/heal on, trigger reminders on, warnings on) and Q25 (auto-pass, hold priority).
 * Parsed with zod at the database boundary; unknown or broken values fall back to the defaults.
 */
import type { SeatSettings } from '@play/protocol';
import { z } from 'zod';

export const AnimationSpeedSchema = z.enum(['slow', 'normal', 'fast']);
export type AnimationSpeed = z.infer<typeof AnimationSpeedSchema>;

/** How fast the practice bot acts (pacing only: `@play/bot` `botDelay`). */
export const BotSpeedSchema = z.enum(['slow', 'normal', 'fast', 'instant']);
export type BotSpeedSetting = z.infer<typeof BotSpeedSchema>;

export const ReducedMotionSchema = z.enum(['system', 'on', 'off']);
export type ReducedMotionSetting = z.infer<typeof ReducedMotionSchema>;

/** Touch interaction mode: `auto` follows the pointer (on for touch screens), `on` / `off` force it. */
export const TouchModeSchema = z.enum(['auto', 'on', 'off']);
export type TouchModeSetting = z.infer<typeof TouchModeSchema>;

const level = (fallback: number) => z.number().min(0).max(1).catch(fallback);

/** One sound channel: switched on, at a level. */
const SoundChannelSchema = (on: boolean, volume: number) =>
  z.object({ on: z.boolean().catch(on), volume: level(volume) }).catch({ on, volume });

/** The defaults of the sound mixer: attention on at 60%, feedback off. */
export const DEFAULT_SOUND = {
  muted: false,
  master: 0.8,
  attention: { on: true, volume: 0.6 },
  feedback: { on: false, volume: 0.5 },
} as const;

/** The sound mixer settings. */
export const SoundSettingsSchema = z.object({
  muted: z.boolean().catch(DEFAULT_SOUND.muted),
  master: level(DEFAULT_SOUND.master),
  attention: SoundChannelSchema(DEFAULT_SOUND.attention.on, DEFAULT_SOUND.attention.volume),
  feedback: SoundChannelSchema(DEFAULT_SOUND.feedback.on, DEFAULT_SOUND.feedback.volume),
});
export type SoundSettings = z.infer<typeof SoundSettingsSchema>;

/** The `settings` jsonb of `play.profile`. */
export const PlaySettingsSchema = z.object({
  automation: z
    .object({
      autoReady: z.boolean().catch(true),
      autoDraw: z.boolean().catch(true),
      autoChannel: z.boolean().catch(true),
      autoHeal: z.boolean().catch(true),
      autoPaySuggestion: z.boolean().catch(true),
      triggerReminders: z.boolean().catch(true),
      warnings: z.boolean().catch(true),
    })
    .catch({
      autoReady: true,
      autoDraw: true,
      autoChannel: true,
      autoHeal: true,
      autoPaySuggestion: true,
      triggerReminders: true,
      warnings: true,
    }),
  /** Q25: auto-pass empty priority windows (only ever when holding no hidden cards; enforced by the server). */
  autoPass: z.boolean().catch(true),
  /** Q25: keep priority after your own play instead of auto-passing. */
  holdPriority: z.boolean().catch(false),
  animationSpeed: AnimationSpeedSchema.catch('normal'),
  reducedMotion: ReducedMotionSchema.catch('system'),
  /**
   * "Touch: Tap to inspect" (`settings/touch.ts`): a tap opens the card sheet with its main actions and a drag needs
   * a short hold first. `auto` turns it on for coarse (touch) pointers; `off` keeps direct tap-to-select and drag.
   */
  touchMode: TouchModeSchema.catch('auto'),
  /** Practice bot speed: Slow 1.4×, Normal (a beat per public play), Fast 0.4×, Instant. Also scales action cues. */
  botSpeed: BotSpeedSchema.catch('normal'),
  /** Play a short soft chime when the turn (or a decision) comes to you. */
  soundOnTurn: z.boolean().catch(false),
  /** Mark the tab title ("● Your turn") when the turn or a decision comes to you while you look elsewhere. */
  titleAlert: z.boolean().catch(true),
  /**
   * The sound mixer (batch 3 #23; `lib/audio.ts`): a master level, the "attention" chime (on at 60% by
   * default; supersedes `soundOnTurn`, kept for older saved settings) and soft "game feedback" clicks for
   * draws, plays, scores and damage (off by default), plus a mute switch. Levels are 0-1.
   */
  sound: SoundSettingsSchema.catch(DEFAULT_SOUND),
});
export type PlaySettings = z.infer<typeof PlaySettingsSchema>;

/** Everything the settings page edits: the tier column plus the jsonb. */
export interface PlayProfile {
  defaultTier: 1 | 2 | 3;
  settings: PlaySettings;
}

/** Q5 / Q25 defaults. */
export const DEFAULT_PROFILE: PlayProfile = {
  defaultTier: 2,
  settings: PlaySettingsSchema.parse({}),
};

/** Parses a `play.profile` row (or anything) into a profile, falling back field by field. */
export function parseProfile(row: unknown): PlayProfile {
  const r = (typeof row === 'object' && row !== null ? row : {}) as { default_tier?: unknown; settings?: unknown };
  const tier = r.default_tier === 1 || r.default_tier === 2 || r.default_tier === 3 ? r.default_tier : 2;
  const settings = PlaySettingsSchema.safeParse(r.settings ?? {});
  return { defaultTier: tier, settings: settings.success ? settings.data : DEFAULT_PROFILE.settings };
}

/** The engine seat settings these preferences imply (sent to the server through `play.profile`). */
export function seatSettingsOf(s: PlaySettings): SeatSettings {
  return { ...s.automation, holdPriority: s.holdPriority };
}

/** Animation duration multiplier for a speed. */
export function speedFactor(speed: AnimationSpeed): number {
  return speed === 'slow' ? 1.5 : speed === 'fast' ? 0.55 : 1;
}
